/**
 * Snipe-IT API client.
 * All calls go through the user's configured Snipe-IT server.
 */

import { getSetting } from './settings.js';

function headers() {
  return {
    Authorization: `Bearer ${getSetting('apiKey')}`,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
}

function baseUrl() {
  // In dev mode, route through the Vite proxy (/snipeit-proxy → VITE_SNIPEIT_URL)
  // to avoid browser CORS restrictions. Set VITE_SNIPEIT_URL in .env.local.
  if (import.meta.env.DEV && import.meta.env.VITE_SNIPEIT_URL) {
    return '/snipeit-proxy';
  }
  return getSetting('serverUrl').replace(/\/+$/, '');
}

// Snipe-IT often returns HTTP 200 with {"status":"error","messages":...} for
// validation/logical failures. messages can be a string (checkout/checkin/lookups)
// or an object keyed by field name (create/update endpoints).
function extractErrorMessage(messages) {
  if (!messages) return 'Unknown error';
  if (typeof messages === 'string') return messages;
  if (typeof messages === 'object') {
    return Object.entries(messages)
      .flatMap(([field, errors]) =>
        Array.isArray(errors) ? errors.map((e) => `${field}: ${e}`) : [`${field}: ${errors}`]
      )
      .join(', ');
  }
  return String(messages);
}

async function request(method, path, body = null) {
  const url = `${baseUrl()}/api/v1${path}`;
  const opts = { method, headers: headers() };
  if (body) opts.body = JSON.stringify(body);

  const res = await fetch(url, opts);
  const json = await res.json();

  if (!res.ok) {
    const msg = json.messages || json.error || res.statusText;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  // HTTP 200 but body signals a failure — validation errors, business logic
  // rejections (e.g. asset already checked out), etc.
  if (json?.status === 'error') {
    throw new Error(extractErrorMessage(json.messages));
  }

  return json;
}

// Lookup variant for byserial / bytag: Snipe-IT returns HTTP 200 + status:error
// when the record is simply not found — that is an expected outcome, not an
// error worth surfacing. Returns null instead of throwing in that case.
async function requestLookup(path) {
  const url = `${baseUrl()}/api/v1${path}`;
  const res = await fetch(url, { method: 'GET', headers: headers() });
  const json = await res.json();

  if (res.status === 404 || json?.status === 'error') return null;

  if (!res.ok) {
    const msg = json.messages || json.error || res.statusText;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  return json;
}

// ── Assets ──

export function searchAssets(query) {
  return request('GET', `/hardware?search=${encodeURIComponent(query)}&limit=20`);
}

export function browseAssets({
  search = '',
  status_id,
  category_id,
  model_id,
  manufacturer_id,
  location_id,
  company_id,
  sort = 'asset_tag',
  order = 'asc',
  offset = 0,
  limit = 50,
} = {}) {
  const params = new URLSearchParams({ sort, order, offset: String(offset), limit: String(limit) });
  if (search)          params.set('search', search);
  if (status_id)       params.set('status_id', status_id);
  if (category_id)     params.set('category_id', category_id);
  if (model_id)        params.set('model_id', model_id);
  if (manufacturer_id) params.set('manufacturer_id', manufacturer_id);
  if (location_id)     params.set('location_id', location_id);
  if (company_id)      params.set('company_id', company_id);
  return request('GET', `/hardware?${params}`);
}

export function getAssetsWithPrefix(prefix) {
  return request('GET', `/hardware?search=${encodeURIComponent(prefix)}&sort=asset_tag&order=desc&limit=100`);
}

export function getAsset(id) {
  return request('GET', `/hardware/${id}`);
}

export function getAssetByTag(tag) {
  return requestLookup(`/hardware/bytag/${encodeURIComponent(tag)}`);
}

export function getAssetBySerial(serial) {
  return requestLookup(`/hardware/byserial/${encodeURIComponent(serial)}`);
}

export function createAsset(data) {
  // data: { name, asset_tag, model_id, status_id, serial, ... }
  return request('POST', '/hardware', data);
}

export async function uploadAssetFile(assetId, file, notes = '') {
  const url = `${baseUrl()}/api/v1/hardware/${assetId}/files`;
  const form = new FormData();
  form.append('file[]', file, file.name || 'creation-image.jpg');
  if (notes) form.append('notes', notes);

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${getSetting('apiKey')}`,
      Accept: 'application/json',
    },
    body: form,
  });

  const json = await res.json();
  if (!res.ok) {
    const msg = json.messages || json.error || res.statusText;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  if (json?.status === 'error') {
    throw new Error(extractErrorMessage(json.messages));
  }
  return json;
}

export function updateAsset(id, data) {
  return request('PATCH', `/hardware/${id}`, data);
}

export function checkoutAsset(id, data) {
  // data: { checkout_to_type: 'user', assigned_user: userId }
  return request('POST', `/hardware/${id}/checkout`, data);
}

export function checkinAsset(id, data = {}) {
  return request('POST', `/hardware/${id}/checkin`, data);
}

// ── Fieldsets ──

export function getFieldsets() {
  return request('GET', '/fieldsets?limit=100');
}

// ── Models ──

export function getModels(search = '') {
  const q = search ? `?search=${encodeURIComponent(search)}&limit=50` : '?limit=50';
  return request('GET', `/models${q}`);
}

export function getModel(id) {
  return request('GET', `/models/${id}`);
}

export function createModel(data) {
  // data: { name, category_id, manufacturer_id }
  return request('POST', '/models', data);
}

// ── Categories ──

export function getCategories(search = '') {
  const q = search ? `?search=${encodeURIComponent(search)}&limit=50` : '?limit=50';
  return request('GET', `/categories${q}`);
}

export function createCategory(data) {
  // data: { name, category_type: 'asset' }
  return request('POST', '/categories', data);
}

// ── Manufacturers ──

export function getManufacturers(search = '') {
  const q = search ? `?search=${encodeURIComponent(search)}&limit=50` : '?limit=50';
  return request('GET', `/manufacturers${q}`);
}

export function createManufacturer(data) {
  // data: { name }
  return request('POST', '/manufacturers', data);
}

// ── Status Labels ──

export function getStatusLabels() {
  return request('GET', '/statuslabels?limit=100');
}

// ── Locations ──

export function getLocations(search = '') {
  const q = search ? `?search=${encodeURIComponent(search)}&limit=50` : '?limit=50';
  return request('GET', `/locations${q}`);
}

// ── Users ──

export function getUsers(search = '') {
  const q = search ? `?search=${encodeURIComponent(search)}&limit=20` : '?limit=20';
  return request('GET', `/users${q}`);
}

export function createUser(data) {
  // data: { first_name, last_name, username, password, password_confirmation }
  return request('POST', '/users', data);
}
